import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Loader container variants
 */
export const loaderVariants = cva(['flex', 'items-center', 'justify-center'], {
  variants: {
    inline: {
      true: ['inline-flex'],
      false: ['flex-col', 'gap-sm'],
    },
  },
  defaultVariants: {
    inline: false,
  },
});

/**
 * Spinner container variants
 */
export const spinnerVariants = cva(['relative', 'shrink-0'], {
  variants: {
    size: {
      s: [],
      m: [],
      l: [],
    },
    inline: {
      true: [],
      false: [],
    },
  },
  compoundVariants: [
    // Inline sizes
    { size: 's', inline: true, class: 'size-[12px]' },
    { size: 'm', inline: true, class: 'size-[16px]' },
    { size: 'l', inline: true, class: 'size-[24px]' },
    // Non-inline sizes (all same: 64px)
    { size: 's', inline: false, class: 'size-[64px]' },
    { size: 'm', inline: false, class: 'size-[64px]' },
    { size: 'l', inline: false, class: 'size-[64px]' },
  ],
  defaultVariants: {
    size: 'm',
    inline: false,
  },
});

/**
 * Spinner SVG variants
 */
export const spinnerSvgVariants = cva(['absolute', 'inset-0', 'w-full', 'h-full'], {
  variants: {
    active: {
      true: ['animate-spin'],
      false: [],
    },
    type: {
      indeterminate: [],
      determinate: [],
      'determinate-value': [],
    },
  },
  compoundVariants: [
    // Only indeterminate type should spin
    {
      type: 'determinate',
      class: 'animate-none',
    },
    {
      type: 'determinate-value',
      class: 'animate-none',
    },
  ],
  defaultVariants: {
    active: true,
    type: 'indeterminate',
  },
});

/**
 * Spinner circle (background) variants
 * Uses stroked circle for background track
 */
export const spinnerCircleVariants = cva([], {
  variants: {},
  defaultVariants: {},
});

/**
 * Spinner progress (foreground) variants
 * Uses stroked circle for progress arc
 */
export const spinnerProgressVariants = cva(['transition-all', 'duration-300', 'ease-in-out'], {
  variants: {
    type: {
      indeterminate: [],
      determinate: [],
      'determinate-value': [],
    },
  },
  defaultVariants: {
    type: 'indeterminate',
  },
});

/**
 * Loader label variants
 */
export const loaderLabelVariants = cva(
  ['text-[12px]', 'leading-[18px]', 'text-foreground', 'text-center', 'whitespace-nowrap'],
  {
    variants: {},
    defaultVariants: {},
  }
);

/**
 * Loader value (percentage) variants
 */
export const loaderValueVariants = cva(
  [
    'absolute',
    'top-1/2',
    'left-1/2',
    '-translate-x-1/2',
    '-translate-y-1/2',
    'text-[14px]',
    'leading-[20px]',
    'text-foreground',
    'whitespace-nowrap',
  ],
  {
    variants: {},
    defaultVariants: {},
  }
);

export type LoaderVariants = VariantProps<typeof loaderVariants>;
export type SpinnerVariants = VariantProps<typeof spinnerVariants>;
export type SpinnerSvgVariants = VariantProps<typeof spinnerSvgVariants>;
export type SpinnerCircleVariants = VariantProps<typeof spinnerCircleVariants>;
export type SpinnerProgressVariants = VariantProps<typeof spinnerProgressVariants>;
export type LoaderLabelVariants = VariantProps<typeof loaderLabelVariants>;
export type LoaderValueVariants = VariantProps<typeof loaderValueVariants>;
